import React, { useCallback, FC } from 'react'
import useToggler from 'core/hooks/useToggler'
import { ThemeProvider } from '@material-ui/styles'
import { useCustomTheme } from 'core/themes/ThemeManager'
import GridDefaultActionButton, {
  GridDefaultActionButtonProps,
} from 'core/elements/grid/buttons/GridDefaultActionButton'
import { useModal } from 'app/core/context/ModalContext'

export interface GridDialogProps<T> {
  onClose: (success?: boolean) => void
  rows: T[]
}

/**
 * @summary This function is used to render the dialog button out of grid. Its the variant of gridDialogButton
 * @param DialogComponent
 * @param customDialogProps
 * @param customButtonProps
 * @param refreshFn
 * @returns Returns a dialog with button wrapper to toggle the dialog open/close
 */
export default function GetDialogButton<T, DialogProps extends GridDialogProps<T>>(
  DialogComponent: FC<DialogProps>,
  customDialogProps?: DialogProps,
  customButtonProps?: Partial<GridDefaultActionButtonProps<T>>,
  refreshFn?: () => void,
): FC<GridDefaultActionButtonProps<T>> {
  return ({ children, closeDropdown, ...buttonProps }) => {
    const [theme] = useCustomTheme()
    const [dialogOpened, toggleDialogOpened, setDialogOpened] = useToggler()
    const { openModal, closeModal } = useModal()

    const handleDialogOpen = useCallback(() => {
      toggleDialogOpened()
      openModal()
    }, [toggleDialogOpened, openModal])

    const handleDialogClose = useCallback(() => {
      if (refreshFn) {
        refreshFn()
      }
      closeDropdown && closeDropdown()
      setDialogOpened(false)
      closeModal()
    }, [refreshFn, closeModal])
    return (
      <>
        <GridDefaultActionButton
          {...buttonProps}
          {...customButtonProps}
          // The onClick here overwrites the one passed from buttonProps...
          onClick={handleDialogOpen}
        >
          {children}
        </GridDefaultActionButton>
        {dialogOpened && (
          <ThemeProvider theme={theme}>
            <DialogComponent
              onClose={handleDialogClose}
              // We have to trick the TS engine here as it is unable to infer extraProps as the remaining props
              {...((customDialogProps || {}) as DialogProps)}
            />
          </ThemeProvider>
        )}
      </>
    )
  }
}
